"use client"

import React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { XCircle } from 'lucide-react'

// One rendered pill = one applied filter (global, stage, or temporary scope).
export interface AppliedFilterPill {
  key: string
  scope: 'global' | 'stage' | 'temporary'
  /** Localized column name, bolded in the pill. */
  label: string
  /** Short value preview, e.g. `New, OnHold` or `"acme"` or `06-01-2026 → 06-30-2026`. */
  preview: string
  /** Overflow count beyond the previewed values (renders as `+N`). */
  overflow: number
  onRemove: () => void
  /**
   * Click-to-edit, mirroring the view designer's pills: clicking the pill body
   * opens that filter's editor (column funnel popover for stage filters, the
   * Filter sidebar for global filters). Omit to render a non-clickable pill.
   */
  onClick?: () => void
}

// Parse the JSON metadata persisted alongside each filter clause
// ({ isSelectAll, hasBlanks, regularValues } | { search } | { start, end })
// into a short preview. Mirrors the view designer's Active Filters Overview
// (view-manager/steps/filters-step.tsx getFilterPreview) — duplicated here
// because that helper is typed against the view-manager draft DTOs; keep the
// two in sync if the persisted `values` shape ever changes.
export function getFilterValuesPreview(
  rawValues: unknown,
  // Optional code→label mapper applied to enumerated values (regularValues),
  // so pills show "Expiring Soon" instead of "ExpiringSoon". Search text and
  // date ranges pass through untouched.
  mapValue?: (value: string) => string,
): { text: string; overflow: number } {
  let meta: any = null
  if (rawValues) {
    try {
      meta = typeof rawValues === 'string' ? JSON.parse(rawValues) : rawValues
    } catch {
      meta = null
    }
  }
  // Tolerate raw primitive / array values — filters persisted before writers
  // adopted the metadata shape (and any writer that still stores the bare
  // value) should preview as that value rather than an empty pill.
  if (meta == null && rawValues != null) {
    if (typeof rawValues === 'string' || typeof rawValues === 'number') {
      meta = { regularValues: [String(rawValues)] }
    } else if (Array.isArray(rawValues)) {
      meta = { regularValues: rawValues.filter(v => v != null).map(String) }
    }
  } else if (typeof meta === 'number') {
    meta = { regularValues: [String(meta)] }
  } else if (Array.isArray(meta)) {
    meta = { regularValues: meta.filter(v => v != null).map(String) }
  }
  if (meta?.start || meta?.end) {
    const parts: string[] = []
    if (meta.start) parts.push(meta.start)
    if (meta.end && meta.end !== meta.start) parts.push(meta.end)
    return { text: parts.join(' → '), overflow: 0 }
  }
  if (meta?.search) return { text: `"${meta.search}"`, overflow: 0 }
  if (meta?.isSelectAll) return { text: 'All', overflow: 0 }
  const vals = (meta?.regularValues || []).map((v: any) => (mapValue ? mapValue(String(v)) : String(v)))
  if (meta?.hasBlanks) vals.push('(blank)')
  if (vals.length === 0) return { text: '', overflow: 0 }
  return { text: vals.slice(0, 2).join(', '), overflow: Math.max(0, vals.length - 2) }
}

// Scope colors, deliberately pastel/muted so the pills read as quiet metadata:
// soft blue = global (view-level) filters, light lilac = stage (column)
// filters, sage green = the transient "Showing: <row>" filter applied by
// cross-tab navigation (global search handoff, VmsEmail → linked request) —
// not part of any saved view. Hue mapping matches the view designer's
// blue/purple convention, just desaturated. Warm hues (orange/peach/red) are
// intentionally avoided — they read as warnings/errors on a passive tag.
const SCOPE_STYLES: Record<AppliedFilterPill['scope'], { background: string; border: string; color: string }> = {
  global: { background: '#F5F9FE', border: '1px solid #DEEAF8', color: '#4674A8' },
  stage: { background: '#FAF8FE', border: '1px solid #E9E3F7', color: '#75649E' },
  temporary: { background: '#F4FAF6', border: '1px solid #DFF0E4', color: '#55855F' },
}

function Pill({ pill, dense }: { pill: AppliedFilterPill; dense: boolean }) {
  const style = SCOPE_STYLES[pill.scope]
  const valueText = pill.overflow > 0 ? `${pill.preview} +${pill.overflow}` : pill.preview
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 4px 3px 8px',
        borderRadius: 9999,
        fontSize: dense ? 13 : 14,
        lineHeight: '18px',
        maxWidth: dense ? 190 : 320,
        whiteSpace: 'nowrap',
        cursor: pill.onClick ? 'pointer' : undefined,
        ...style,
      }}
      title={`${pill.label}: ${valueText}`}
      onClick={pill.onClick}
      role={pill.onClick ? 'button' : undefined}
    >
      <span style={{ fontWeight: 600, flexShrink: 0 }}>{pill.label}:</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{valueText}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); pill.onRemove() }}
        aria-label={`Remove filter ${pill.label}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: 9999,
          border: 'none',
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          flexShrink: 0,
          padding: 0,
        }}
      >
        <XCircle style={{ width: 14, height: 14 }} />
      </button>
    </span>
  )
}

/**
 * Full pill list, sectioned by scope when `groupLabels` is provided:
 * "Global Filters" over the blue pills, "<Stage> stage filters" over the
 * purple ones. Used by the +N expansion popover and the Filter sidebar's
 * Active-filters block so both read the same way.
 */
export function GroupedPillList({
  pills,
  groupLabels,
}: {
  pills: AppliedFilterPill[]
  groupLabels?: { global?: string; stage?: string }
}) {
  // The transient "Showing" pill leads ungrouped — its label already says
  // what it is, and it isn't part of the view's global/stage filter model.
  const temporaryPills = pills.filter(p => p.scope === 'temporary')
  const globalPills = pills.filter(p => p.scope === 'global')
  const stagePills = pills.filter(p => p.scope === 'stage')
  const groups: { label?: string; items: AppliedFilterPill[] }[] = groupLabels
    ? [
        { items: temporaryPills },
        { label: groupLabels.global, items: globalPills },
        { label: groupLabels.stage, items: stagePills },
      ].filter(g => g.items.length > 0)
    : [{ items: pills }]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {groups.map((group, i) => (
        <div key={group.label ?? i}>
          {group.label && (
            <div style={{ fontSize: 12, fontWeight: 600, color: '#02010D99', marginBottom: 6 }}>
              {group.label}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {group.items.map(pill => (
              <Pill key={pill.key} pill={pill} dense={false} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Compact "what's filtering this grid right now" strip. Toolbar mode shows the
 * first `maxVisible` pills plus a `+N` chip that opens a popover with the FULL
 * sectioned list — the hub toolbar is a single non-wrapping flex row, so the
 * strip must have a hard width ceiling.
 */
export function AppliedFilterPills({
  pills,
  maxVisible = 2,
  dense = true,
  groupLabels,
}: {
  pills: AppliedFilterPill[]
  maxVisible?: number
  dense?: boolean
  groupLabels?: { global?: string; stage?: string }
}) {
  if (pills.length === 0) return null
  const visible = pills.slice(0, maxVisible)
  const hidden = pills.slice(maxVisible)
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: dense ? 'nowrap' : 'wrap' }}>
      {visible.map(pill => (
        <Pill key={pill.key} pill={pill} dense={dense} />
      ))}
      {hidden.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`${pills.length} filters applied`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 8px',
                borderRadius: 9999,
                border: '1px solid #BFC6D7',
                background: '#F1F3F8',
                color: '#0F214B',
                fontSize: dense ? 13 : 14,
                lineHeight: '18px',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              +{hidden.length}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" style={{ width: 'auto', maxWidth: 420, padding: 12, zIndex: 1001 }}>
            {/* The expansion shows EVERY filter on this page (not just the
                overflow) so it reads as the page's complete filter list. */}
            <GroupedPillList pills={pills} groupLabels={groupLabels} />
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
